import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { map, skip, take } from 'rxjs/operators';
import { DsBannerState, DsModule } from '@levelaccess/design-system';

import { DigitalPropertyService } from '../../../../services/digital-property.service';
import {
  IDigitalProperty,
  IDigitalPropertyDetails,
  IDigitalPropertyType,
} from '../../../../../../shared/interfaces/digital-property.interface';
import { BusMessageChannels, BusMessageService } from '../../../../services/bus-message.service';
import { NotificationPosition } from '../../../../models/notification.model';
import { UserService } from '../../../../services/user.service';
import { NotificationService } from '../../../../services/notification.service';
import { TranslateService } from '../../../../translate/translate.service';
import { ErrorMessageService } from '../../../../services/error-message.service';
import { IComponentFormState } from '../../../../interfaces/advanced-scan-form-state.interface';
import { $digitalProperty, $digitalPropertyType } from '../../../../../../shared/constants/digital-properties';
import { IUserServerResponse } from '../../../../../../shared/interfaces/user.interface';
import { AclSecurityAdapter } from '../../../../services/acl.service';
import { IHttpErrorResponse } from '../../../../interfaces/error.interface';
import { ModalService } from '../../../../services/modal.service';
import { PropertyTypeChangeConfirmationComponent } from './property-type-change-confirmation/property-type-change-confirmation.component';
import { LoadErrorHandler } from '../../../../services/load-error-handler';
import { errorMessagesNames } from '../../../../../../shared/constants/errors';
import { DigitalPropertyWorkspaceFilterComponent } from '../../../../components/digital-property-workspace-filter/digital-property-workspace-filter.component';
import { FunctionalArea, SecureAction } from '../../../../../../shared/constants/security';
import { UserAclService } from '../../../../services/user-acl.service';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';
import { IMobileApp } from '../../../../../../shared/interfaces/mobile-app.interface';
import { MobileAppUploadStatus } from '../../../../../../shared/constants/mobile-app';
import { RequiredSecurities } from '../../../../../../shared/constants/required-securities';
import { IRequiredSecurity } from '../../../../../../shared/interfaces/security.interface';
import { DigitalPropertyTypeUtility } from '../../../../../../shared/utils/digital-property-type.utility';

import { SharedModule } from '../../../../shared/shared.module';
import { PortfolioSharedModule } from '../portfolio-shared.module';
import { EmulatorAppTableModule } from '../../mobile-testing-emulator/emulator-app-table/emulator-app-table.module';

enum DigitalPropertyForm {
  propertyDetails = 'propertyDetails',
}

@Component({
  selector: 'app-edit-digital-property',
  templateUrl: './edit-digital-property.component.html',
  styleUrls: ['./edit-digital-property.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, DsModule, PortfolioSharedModule, EmulatorAppTableModule],
})
export class EditDigitalPropertyComponent implements OnInit, OnDestroy {
  public readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
  public readMobileAppsRequiredSecurity: IRequiredSecurity;

  private subscriptions: Subscription;
  private formStates: Partial<Record<DigitalPropertyForm, IComponentFormState<IDigitalProperty>>>;

  @Input() workspaceId: string;
  @Input() digitalPropertyId: string;

  @Output() digitalPropertyEdited: EventEmitter<IDigitalPropertyDetails> = new EventEmitter<IDigitalPropertyDetails>();
  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();
  @Output() userSwitchedDigitalProperty: EventEmitter<void> = new EventEmitter<void>();

  public formValidationRequest: Subject<void>;
  public form: UntypedFormGroup;
  public saveError: HttpErrorResponse;
  public digitalProperty: IDigitalPropertyDetails;
  public canEditProperty$: Observable<boolean>;
  public canEditName$: Observable<boolean>;
  public canEditTool$: Observable<boolean>;
  public saveDisabled$: Observable<boolean>;
  public isMobile: boolean = false;
  public $isUploading: WritableSignal<boolean> = signal(false);

  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;

  constructor(
    private element: ElementRef<HTMLElement>,
    private digitalPropertyService: DigitalPropertyService,
    private userService: UserService,
    private busMessageService: BusMessageService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private errorMessageService: ErrorMessageService,
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: ModalService,
    private loadErrorHandler: LoadErrorHandler,
    private userAclService: UserAclService,
  ) {
    this.formStates = {};
    this.subscriptions = new Subscription();
    this.formValidationRequest = new Subject<void>();
    this.form = this.formBuilder.group({});
    this.readMobileAppsRequiredSecurity = RequiredSecurities.AT_Mobile_Apps_Read;
  }

  private onUpdateDigitalPropertySuccess(property: IDigitalPropertyDetails): void {
    this.userService.reloadUser();
    this.busMessageService.to(BusMessageChannels.digitalPropertyChange).next();

    this.digitalProperty = {
      ...this.digitalProperty,
      ...property,
    };

    this.notificationService.success(
      this.translateService.instant('update_digital_property_success', property.name),
      NotificationPosition.Toast,
      true,
    );

    this.digitalPropertyEdited.emit(property);
  }

  private handleError(message: string, response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError(message, response, EditDigitalPropertyComponent.name);
    this.saveError = response.error;
    this.changeDetectorRef.detectChanges();
  }

  private onGetDigitalPropertySuccess(digitalProperty: IDigitalPropertyDetails): void {
    this.digitalProperty = digitalProperty;
    this.isMobile = DigitalPropertyTypeUtility.isMobile(digitalProperty[$digitalProperty.type]);
    this.changeDetectorRef.detectChanges();
  }

  private onGetDigitalPropertyError(response: HttpErrorResponse): void {
    this.handleError('get_digital_property_error', response);
  }

  private onUpdateDigitalPropertyError(response: IHttpErrorResponse): void {
    if (response?.error?.app?.name === errorMessagesNames.DigitalPropertyInvalidDomains) {
      this.handleError('cannot_update_digital_property_invalid_domains', response);
      return;
    }

    this.handleError('update_digital_property_error', response);
  }

  private onUserData(user: IUserServerResponse): void {
    this.canEditName$ = this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useWorkspaceId(this.workspaceId)
          .useDigitalPropertyId(this.digitalPropertyId)
          .useFunctionalActions({
            [FunctionalArea.digital_properties]: [SecureAction.read, SecureAction.update],
          })
          .check(),
      ),
    );
    this.canEditProperty$ = this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useWorkspaceId(this.workspaceId)
          .useDigitalPropertyId(this.digitalPropertyId)
          .useFunctionalActions({
            [FunctionalArea.digital_properties]: [SecureAction.read, SecureAction.update],
          })
          .check(),
      ),
    );
    this.canEditTool$ = this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useWorkspaceId(this.workspaceId)
          .useDigitalPropertyId(this.digitalPropertyId)
          .useFunctionalActions({
            [FunctionalArea.digital_properties]: [SecureAction.read, SecureAction.update],
          })
          .check(),
      ),
    );

    this.saveDisabled$ = combineLatest([this.canEditProperty$, this.canEditName$]).pipe(
      map(([canEditProperty, canEditName]: [boolean, boolean]) => !canEditProperty && !canEditName),
    );
  }

  private async saveWithTypeCheck(): Promise<void> {
    const oldType: IDigitalPropertyType = this.digitalProperty[$digitalProperty.type];
    const newProperty: IDigitalProperty = this.formStates.propertyDetails.objectData;

    if (oldType?.[$digitalPropertyType._id] !== newProperty[$digitalProperty.typeId]) {
      const component: PropertyTypeChangeConfirmationComponent = this.modalService.open(
        PropertyTypeChangeConfirmationComponent,
      ).instance;
      await component.container.onClose$.pipe(take(1)).toPromise();

      if (component.confirmed) {
        this.saveDigitalProperty();
      }
    } else {
      this.saveDigitalProperty();
    }
  }

  private getDigitalPropertyData(): void {
    this.subscriptions.add(
      this.digitalPropertyService.getDigitalProperty(this.workspaceId, this.digitalPropertyId).subscribe({
        next: this.onGetDigitalPropertySuccess.bind(this),
        error: this.onGetDigitalPropertyError.bind(this),
      }),
    );
  }

  private saveDigitalProperty(): void {
    const digitalProperty: IDigitalProperty = this.formStates.propertyDetails.objectData;
    digitalProperty[$digitalProperty.authorizedDomains] = this.digitalProperty[$digitalProperty.authorizedDomains];
    digitalProperty[$digitalProperty.pagesLimit] = this.digitalProperty[$digitalProperty.pagesLimit];

    this.subscriptions.add(
      this.digitalPropertyService.updateDigitalProperty(digitalProperty, this.digitalPropertyId).subscribe({
        next: this.onUpdateDigitalPropertySuccess.bind(this, digitalProperty),
        error: this.onUpdateDigitalPropertyError.bind(this),
      }),
    );
  }

  public detailsChanged(data: IComponentFormState): void {
    this.form.setControl(DigitalPropertyForm.propertyDetails, data.form);
    this.formStates.propertyDetails = data;
  }

  public onSave(): void {
    this.formValidationRequest.next();

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    this.saveWithTypeCheck().then();
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  public onMobileAppsLoaded(apps: IMobileApp[]): void {
    const app: IMobileApp = apps[0];

    this.$isUploading.set(app?.uploadStatus === MobileAppUploadStatus.PENDING);
  }

  public ngOnInit(): void {
    this.getDigitalPropertyData();

    this.subscriptions.add(this.userService.userDataChanged$.subscribe({ next: this.onUserData.bind(this) }));

    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        if (this.form.dirty) {
          DigitalPropertyWorkspaceFilterComponent.setOnChangeConfirmation(
            this,
            'switch_digital_property_from_portfolio_confirmation_text',
          );
        }
      }),
    );

    this.subscriptions.add(
      this.busMessageService
        .from(BusMessageChannels.userSwitchedToDigitalProperty)
        .pipe(skip(1))
        .subscribe((): void => {
          this.userSwitchedDigitalProperty.emit();
        }),
    );
  }

  public ngOnDestroy(): void {
    DigitalPropertyWorkspaceFilterComponent.setOnChangeConfirmation(this);
    this.subscriptions.unsubscribe();
  }
}
